/**
 * UploadModal — Drag-and-drop document upload dialog (PDF + Office formats).
 *
 * Features:
 *  - Custom drag-and-drop zone with visual drag-over state
 *  - Accepts only the file formats enabled for the selected organization
 *    (org.enabledFileTypes); macro-enabled formats (.xlsm/.docm) always blocked
 *  - Rejected files show an explicit error listing accepted formats (no silent drop)
 *  - Staged files are re-validated when the org selection changes
 *  - Orgs with zero enabled formats get a contact-admin notice and a disabled drop zone
 *  - Staged files list with file icon and file size
 *  - Summary bar with count, total size, and "Clear all"
 *  - Calls onUpload(files) to upload via API
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, File06, UploadCloud02, XClose } from "@untitledui/icons";
import { getReadableFileSize } from "@/components/application/file-upload/file-upload-base";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Button } from "@/components/base/buttons/button";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { Select } from "@/components/base/select/select";
import { SelectItem } from "@/components/base/select/select-item";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icons";
import type { OrganizationListItemDto } from "@/types/api/organizations";
import { cx } from "@/utils/cx";
import {
    ACCEPTED_UPLOAD_FORMATS_LABEL,
    FILE_TYPE_ID_TO_FORMAT,
    UPLOAD_ACCEPT_ATTRIBUTE,
    buildAcceptAttribute,
    classifyUploadFile,
    formatListLabel,
    type UploadFileFormat,
} from "@/utils/file-validation";

/** Friendly format names ("PDF, Excel") for the given formats, deduplicated. */
function formatNamesLabel(formats: ReadonlySet<UploadFileFormat>): string {
    const names: string[] = [];
    if (formats.has("pdf")) names.push("PDF");
    if (formats.has("xls") || formats.has("xlsx")) names.push("Excel");
    if (formats.has("doc") || formats.has("docx")) names.push("Word");
    return names.join(", ");
}

interface UploadModalProps {
    /** Whether the modal is open */
    isOpen: boolean;
    /** Called when the modal should close */
    onClose: () => void;
    /** Called to upload files via API */
    onUpload: (files: File[], organizationId?: number) => Promise<void>;
    /** User's organizations — org dropdown shown only when length > 1 */
    orgs?: OrganizationListItemDto[];
}

interface StagedFile {
    id: string;
    file: File;
}

export function UploadModal({ isOpen, onClose, onUpload, orgs }: UploadModalProps) {
    const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
    const [rejectionError, setRejectionError] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Organization selection for multi-org users
    const isMultiOrg = orgs != null && orgs.length > 1;
    const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);

    // Auto-select single org; reset when modal opens
    useEffect(() => {
        if (isOpen) {
            if (orgs && orgs.length === 1) {
                setSelectedOrgId(orgs[0].id);
            } else {
                setSelectedOrgId(null);
            }
            setRejectionError(null);
        }
    }, [isOpen, orgs]);

    // Formats enabled for the selected org; before a multi-org user picks an org,
    // the union across their orgs (a file must be stageable for at least one).
    // Undefined when no orgs are provided — falls back to the platform-wide set,
    // and the backend gate stays authoritative.
    const allowedFormats = useMemo<ReadonlySet<UploadFileFormat> | undefined>(() => {
        if (orgs == null || orgs.length === 0) return undefined;
        const source = selectedOrgId != null ? orgs.filter((o) => o.id === selectedOrgId) : orgs;
        const formats = new Set<UploadFileFormat>();
        for (const org of source) {
            for (const fileTypeId of org.enabledFileTypes ?? []) {
                const format = FILE_TYPE_ID_TO_FORMAT[fileTypeId];
                if (format) formats.add(format);
            }
        }
        return formats;
    }, [orgs, selectedOrgId]);

    const noFormatsEnabled = allowedFormats != null && allowedFormats.size === 0;
    const acceptedFormatsLabel = allowedFormats != null ? formatListLabel(allowedFormats) : ACCEPTED_UPLOAD_FORMATS_LABEL;

    const handleAddFiles = useCallback(
        (files: File[]) => {
            const accepted: File[] = [];
            const blockedMacro: string[] = [];
            const unsupported: string[] = [];
            const notEnabled: string[] = [];

            for (const file of files) {
                const classification = classifyUploadFile(file, allowedFormats);
                // Discriminant equality (not truthiness) — this tsconfig has strict:false,
                // where truthiness checks don't narrow the union's else branch
                if (classification.accepted === false) {
                    if (classification.reason === "blocked-macro-enabled") {
                        blockedMacro.push(file.name);
                    } else if (classification.reason === "not-enabled-for-org") {
                        notEnabled.push(file.name);
                    } else {
                        unsupported.push(file.name);
                    }
                } else {
                    accepted.push(file);
                }
            }

            // Explicit rejection feedback — never silently drop files
            const errors: string[] = [];
            if (blockedMacro.length > 0) {
                errors.push(`Macro-enabled Office files are not allowed: ${blockedMacro.join(", ")}.`);
            }
            if (notEnabled.length > 0) {
                errors.push(`Not enabled for this organization: ${notEnabled.join(", ")}.`);
            }
            if (unsupported.length > 0) {
                errors.push(`Unsupported file type: ${unsupported.join(", ")}.`);
            }
            setRejectionError(errors.length > 0 ? `${errors.join(" ")} Accepted formats: ${acceptedFormatsLabel || "none"}.` : null);

            if (accepted.length === 0) return;
            const newFiles: StagedFile[] = accepted.map((file) => ({
                id: `${file.name}-${Date.now()}-${Math.random()}`,
                file,
            }));
            setStagedFiles((prev) => [...prev, ...newFiles]);
        },
        [allowedFormats, acceptedFormatsLabel],
    );

    // Re-validate staged files when the org selection (and thus the allowed set)
    // changes — remove now-disallowed files with an explicit message, never silently.
    useEffect(() => {
        if (allowedFormats == null) return;
        const removed = stagedFiles.filter((sf) => classifyUploadFile(sf.file, allowedFormats).accepted === false);
        if (removed.length === 0) return;
        setStagedFiles((prev) => prev.filter((sf) => classifyUploadFile(sf.file, allowedFormats).accepted !== false));
        setRejectionError(
            `Removed (not enabled for this organization): ${removed.map((sf) => sf.file.name).join(", ")}. ` +
                `Accepted formats: ${formatListLabel(allowedFormats) || "none"}.`,
        );
    }, [allowedFormats, stagedFiles]);

    const handleRemoveFile = useCallback((id: string) => {
        setStagedFiles((prev) => prev.filter((f) => f.id !== id));
    }, []);

    const handleUpload = useCallback(async () => {
        if (stagedFiles.length === 0) return;
        setIsUploading(true);
        try {
            await onUpload(
                stagedFiles.map((sf) => sf.file),
                selectedOrgId ?? undefined,
            );
            setStagedFiles([]);
            onClose();
        } catch (err) {
            console.error("Upload failed:", err);
        } finally {
            setIsUploading(false);
        }
    }, [stagedFiles, onUpload, onClose, selectedOrgId]);

    const handleOpenChange = useCallback(
        (open: boolean) => {
            if (!open && !isUploading) {
                setStagedFiles([]);
                setRejectionError(null);
                onClose();
            }
        },
        [onClose, isUploading],
    );

    const handleFileInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const selected = e.target.files ? Array.from(e.target.files) : [];
            if (selected.length > 0) handleAddFiles(selected);
            // Reset input so re-selecting the same file works
            if (fileInputRef.current) fileInputRef.current.value = "";
        },
        [handleAddFiles],
    );

    const totalSize = stagedFiles.reduce((sum, sf) => sum + sf.file.size, 0);

    return (
        <ModalOverlay isOpen={isOpen} onOpenChange={handleOpenChange}>
            <Modal className="max-w-lg">
                <Dialog>
                    <div className="w-full rounded-xl border border-secondary bg-primary shadow-xl">
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 pt-6 pb-0">
                            <div className="flex items-center gap-3">
                                <FeaturedIcon icon={UploadCloud02} color="brand" theme="light" size="md" />
                                <div>
                                    <h2 className="text-lg font-semibold text-primary">Upload Documents</h2>
                                    <p className="text-sm text-tertiary">
                                        {allowedFormats != null && !noFormatsEnabled
                                            ? `Add ${formatNamesLabel(allowedFormats)} files for processing`
                                            : "Add files for processing"}
                                    </p>
                                </div>
                            </div>
                            <Button color="tertiary" size="sm" iconLeading={XClose} onClick={() => handleOpenChange(false)} aria-label="Close" />
                        </div>

                        {/* Organization selector — only for multi-org users */}
                        {isMultiOrg && (
                            <div className="px-6 pt-5 pb-0">
                                <Select
                                    label="Organization"
                                    selectedKey={selectedOrgId != null ? String(selectedOrgId) : null}
                                    onSelectionChange={(key) => setSelectedOrgId(key != null ? Number(key) : null)}
                                    placeholder="Select organization..."
                                    size="sm"
                                >
                                    {orgs!.map((org) => (
                                        <SelectItem key={String(org.id)} id={String(org.id)}>
                                            {org.name}
                                        </SelectItem>
                                    ))}
                                </Select>
                            </div>
                        )}

                        {/* Drop Zone */}
                        <div className="px-6 pt-5 pb-2">
                            {noFormatsEnabled && (
                                <div className="mb-3 flex items-start gap-2 rounded-lg border border-error_subtle bg-error-primary px-3 py-2">
                                    <AlertCircle className="mt-0.5 size-4 shrink-0 text-fg-error-primary" />
                                    <p className="text-xs text-error-primary">
                                        No file formats are enabled for this organization. Contact your administrator.
                                    </p>
                                </div>
                            )}
                            <div
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    if (!noFormatsEnabled) setIsDragOver(true);
                                }}
                                onDragLeave={(e) => {
                                    e.preventDefault();
                                    setIsDragOver(false);
                                }}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    setIsDragOver(false);
                                    if (!noFormatsEnabled) handleAddFiles(Array.from(e.dataTransfer.files));
                                }}
                                onClick={() => {
                                    if (!noFormatsEnabled) fileInputRef.current?.click();
                                }}
                                className={cx(
                                    "flex flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-8 transition-colors",
                                    noFormatsEnabled
                                        ? "cursor-not-allowed border-secondary opacity-50"
                                        : isDragOver
                                          ? "cursor-pointer border-brand bg-brand-primary"
                                          : "cursor-pointer border-secondary hover:border-brand hover:bg-primary_hover",
                                )}
                            >
                                <div
                                    className={cx(
                                        "mb-3 flex size-10 items-center justify-center rounded-full",
                                        isDragOver ? "bg-brand-solid text-fg-white" : "bg-secondary",
                                    )}
                                >
                                    <UploadCloud02 className={cx("size-5", isDragOver ? "text-fg-white" : "text-fg-quaternary")} />
                                </div>
                                <p className="text-sm text-tertiary">
                                    <span className="font-semibold text-brand-primary">Click to upload</span> or drag and drop
                                </p>
                                <p className="mt-1 text-xs text-quaternary">
                                    {noFormatsEnabled ? "No formats enabled" : `${acceptedFormatsLabel} files up to 50MB`}
                                </p>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept={allowedFormats != null ? buildAcceptAttribute(allowedFormats) : UPLOAD_ACCEPT_ATTRIBUTE}
                                    multiple
                                    onChange={handleFileInputChange}
                                    className="hidden"
                                />
                            </div>

                            {/* Rejected-files error — explicit feedback, never a silent drop */}
                            {rejectionError && (
                                <div className="mt-3 flex items-start gap-2 rounded-lg border border-error_subtle bg-error-primary px-3 py-2">
                                    <AlertCircle className="mt-0.5 size-4 shrink-0 text-fg-error-primary" />
                                    <p className="text-xs text-error-primary">{rejectionError}</p>
                                </div>
                            )}
                        </div>

                        {/* Staged Files List */}
                        {stagedFiles.length > 0 && (
                            <div className="px-6 pb-2">
                                {/* Summary bar */}
                                <div className="mt-2 flex items-center justify-between rounded-lg bg-brand-primary px-3 py-2">
                                    <p className="text-sm font-medium text-brand-primary">
                                        {stagedFiles.length} {stagedFiles.length === 1 ? "file" : "files"}{" "}
                                        <span className="font-normal text-tertiary">({getReadableFileSize(totalSize)})</span>
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => setStagedFiles([])}
                                        className="text-xs font-medium text-brand-primary transition-colors hover:text-brand-secondary"
                                    >
                                        Clear all
                                    </button>
                                </div>

                                {/* Individual file rows */}
                                <div className="mt-2 custom-scrollbar max-h-48 space-y-1.5 overflow-y-auto">
                                    {stagedFiles.map((sf) => (
                                        <div key={sf.id} className="flex items-center gap-3 rounded-lg border border-secondary bg-secondary px-3 py-2">
                                            <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-error-primary">
                                                <File06 className="size-3.5 text-fg-error-primary" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate text-xs font-medium text-primary">{sf.file.name}</p>
                                                <p className="text-[11px] text-quaternary">{getReadableFileSize(sf.file.size)}</p>
                                            </div>
                                            <ButtonUtility icon={XClose} size="xs" color="tertiary" tooltip="Remove" onClick={() => handleRemoveFile(sf.id)} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Footer */}
                        <div className="flex items-center justify-end gap-3 px-6 py-5">
                            <Button size="md" color="secondary" isDisabled={isUploading} onClick={() => handleOpenChange(false)}>
                                Cancel
                            </Button>
                            <Button
                                size="md"
                                color="primary"
                                iconLeading={UploadCloud02}
                                isDisabled={stagedFiles.length === 0 || isUploading || noFormatsEnabled || (isMultiOrg && selectedOrgId == null)}
                                onClick={handleUpload}
                            >
                                {isUploading ? "Uploading..." : `Upload${stagedFiles.length > 0 ? ` (${stagedFiles.length})` : ""}`}
                            </Button>
                        </div>
                    </div>
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
}
